import { type ComponentProps, useCallback, useEffect, useMemo, useState   } from 'react';
import { ArrowLeft, Building, Home, Loader2, Plus, AlertCircle, Check, Download, X, StickyNote, Trash2 } from 'lucide-react';
import axios from 'axios';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { LookupAutocomplete } from '../ui/lookup-autocomplete';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';

import { useProjectsApi } from '../../services/projects';
import { useSubprojectsApi } from '../../services/subprojects';
import type { DecimalString, RoomItemState, Uuid, SubprojectPayload } from '../../types/subprojects';
import {
  formatProjectStatus,
  type ProjectDetail as ProjectDetailResponse,
  type ProjectFinance,
  type ProjectItem,
  type ProjectStatus,
  type ProjectSubproject,
  type ProjectSubprojectCreatePayload,
} from '../../types/projects';

import { useOrdersApi, useOrderActions } from '../../services/orders';
import { useShippingLocationsApi } from '../../services/shippingLocations';
import type { Order, OrderStatus } from '../../types/orders';
import { useSuppliersApi } from '../../services/suppliers';
import { useCurrentUser } from '../../hook_au/useCurrentUser';
import { useAppSettings } from '../../hook/useAppSettings';
import { OrderEmailDialog } from '../orders/OrderEmailDialog';
import { SubprojectLink } from '../orders/SubprojectLink';

interface ProjectDetailProps {
  projectId: string;
  onBack: () => void;
  onSelectSubproject?: (subprojectId: string) => void;
  onSubprojectUpdated?: () => void;
  refreshVersion?: number;
}

type BadgeVariant = ComponentProps<typeof Badge>['variant'];
type ProjectDetailWithDescription = ProjectDetailResponse & { description?: string | null };
type ProjectOrder = {
  id: string;
  supplier: string;
  items: number;
  total: string | number | null;
  status: string;
};

const PROJECT_ORDERS_COLUMN_WIDTHS = {
  orderRef: '8%',
  subproject: '12%',
  supplier: '10%',
  items: '5%',
  total: '7%',
  status: '7%',
  supplierEmailed: '6%',
  supplierCalled: '6%',
  supplierConfirmed: '6%',
  shipping: '13%',
  tracking: '7%',
  delivery: '7%',
  notes: '4%',
  pdf: '4%',
  actions: '8%',
} as const;

const STATUS_BADGE_VARIANTS: Record<ProjectStatus, BadgeVariant> = {
  planificacion: 'planning',
  en_progreso: 'inProgress',
  presupuestado: 'quoted',
  finalizado: 'finished',
};

const ITEM_STATE_BADGES: Record<RoomItemState, BadgeVariant> = {
  pendiente: 'secondary',
  pedido: 'inProgress',
  recibido: 'quoted',
  instalado: 'finished',
};

const STATUS_LABELS: Record<OrderStatus, string> = {
  borrador: 'Borrador',
  pedido: 'Pedido',
  confirmado: 'Confirmado',
  enviado: 'Enviado',
  recibido: 'Recibido',
  cancelado: 'Cancelado',
};

const STATUS_VARIANTS: Record<OrderStatus, 'secondary' | 'inProgress' | 'quoted' | 'finished' | 'destructive'> = {
  borrador: 'secondary',
  pedido: 'quoted',
  confirmado: 'inProgress',
  enviado: 'inProgress',
  recibido: 'finished',
  cancelado: 'destructive',
};

const getCompletionColor = (completion: number) => {
  if (completion >= 75) return 'bg-green-500';
  if (completion >= 50) return 'bg-blue-500';
  if (completion >= 25) return 'bg-yellow-500';
  return 'bg-gray-300';
};

const ITEM_STATE_PROGRESS: Record<RoomItemState, number> = {
  pendiente: 0,
  pedido: 40,
  recibido: 75,
  instalado: 100,
};

const getApiErrorMessage = (apiError: unknown, fallback: string) => {
  if (axios.isAxiosError(apiError)) {
    const detail = (apiError.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const messages = detail
        .map((entry) =>
          typeof entry === 'string'
            ? entry
            : (entry as { msg?: string; message?: string; detail?: string }).msg ??
              (entry as { message?: string; detail?: string }).message ??
              (entry as { detail?: string }).detail ??
              JSON.stringify(entry),
        )
        .filter(Boolean);
      if (messages.length > 0) return messages.join(' | ');
    }
    if (detail && typeof detail === 'object') {
      const typed = detail as { message?: string; detail?: string };
      return typed.message ?? typed.detail ?? apiError.message;
    }
    return apiError.message;
  }
  if (apiError instanceof Error) return apiError.message;
  return fallback;
};

const toNumber = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export function ProjectDetail({
  projectId,
  onBack,
  onSelectSubproject,
  onSubprojectUpdated,
  refreshVersion,
}: ProjectDetailProps) {
  const { role } = useCurrentUser();
  const {
    settings: appSettings,
    formatCurrency,
    formatPercent,
  } = useAppSettings();
  const isLogistica = role === 'logistica';
  const {
    fetchProjectDetail,
    fetchProjectSubprojects,
    fetchProjectItems,
    fetchProjectFinances,
    createProjectSubproject,
  } = useProjectsApi();

  const { createSubproject , updateSubproject } = useSubprojectsApi();

  const { data: suppliers } = useSuppliersApi({ search: undefined, active: undefined }, { page: 1, page_size: 100 });


  const formatOrderRef = (order: Order) => order.order_number || '—';
const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString(appSettings.currencyLocale) : '—';


    const {
  data: projectOrders,
  loading: ordersLoading,
  error: ordersError,
  pagination: ordersPagination,
  setFilters: setOrderFilters,
  setPagination: setOrdersPagination,
  goToPage: goToOrdersPage,
  refresh: refreshOrders,
} = useOrdersApi({ project: projectId }, { page: 1, page_size: 10 });

const { updateOrder, deleteOrder, downloadOrderPdf, sendOrderEmail } = useOrderActions();

  const [projectDetail, setProjectDetail] = useState<ProjectDetailResponse | null>(null);
  const [subprojects, setSubprojects] = useState<ProjectSubproject[]>([]);
  const [items, setItems] = useState<ProjectItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [finances, setFinances] = useState<ProjectFinance | null>(null);
  const [financesLoading, setFinancesLoading] = useState(false);
  const [financesError, setFinancesError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'subprojects' | 'items' | 'orders' | 'finances'>('subprojects');
  const [projectItems, setProjectItems] = useState<ProjectItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [subprojectsLoading, setSubprojectsLoading] = useState(false);
  const [subprojectsError, setSubprojectsError] = useState<string | null>(null);
  const [editingSubproject, setEditingSubproject] = useState<ProjectSubproject | null>(null);
  const [editForm, setEditForm] = useState<NewSubprojectForm | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [isUpdatingSubproject, setIsUpdatingSubproject] = useState(false);
  const loadSubprojects = useCallback(
    async (id: Uuid) => {
      setSubprojectsLoading(true);
      setSubprojectsError(null);
      try {
        const response = await fetchProjectSubprojects(id);
        const ordered = [...response].sort((a, b) =>
          a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }),
        );
        setSubprojects(ordered);
      } catch (apiError) {
        const message =
          apiError instanceof Error
            ? apiError.message
            : 'No pudimos cargar los subproyectos del proyecto.';
        setSubprojectsError(message);
      } finally {
        setSubprojectsLoading(false);
      }
    },
    [fetchProjectSubprojects],
  );

const [confirmDelete, setConfirmDelete] = useState<Order | null>(null);
const [orderPendingEmail, setOrderPendingEmail] = useState<Order | null>(null);
const [sendingOrderEmail, setSendingOrderEmail] = useState(false);
const [orderPendingCall, setOrderPendingCall] = useState<Order | null>(null);
const [markingSupplierCalled, setMarkingSupplierCalled] = useState(false);
const [orderPendingSupplierConfirmation, setOrderPendingSupplierConfirmation] = useState<Order | null>(null);
const [markingSupplierConfirmed, setMarkingSupplierConfirmed] = useState(false);
const [detailOrder, setDetailOrder] = useState<Order | null>(null);
const [detailForm, setDetailForm] = useState({
  status: 'borrador' as OrderStatus,
  shipping_location_name: '',
  shipping_location_id: '',
  expected_delivery_date: '',
  order_number: '',
  tracking_number: '',
});
const [savingDetail, setSavingDetail] = useState(false);
const [submitting, setSubmitting] = useState(false);

const [notesPreview, setNotesPreview] = useState<Order | null>(null);
const [noteDraft, setNoteDraft] = useState('');
const [savingNote, setSavingNote] = useState(false);
const [downloadingPdfId, setDownloadingPdfId] = useState<string | null>(null);
const [orderPageInput, setOrderPageInput] = useState('1');
const {
  shippingLocations,
  loading: shippingLocationsLoading,
  refresh: refreshShippingLocations,
} = useShippingLocationsApi({ active: true, limit: 200 });

useEffect(() => {
  if (!detailOrder) return;
  setDetailForm({
    status: detailOrder.status,
    shipping_location_name: detailOrder.shipping_location ?? '',
    shipping_location_id: detailOrder.shipping_location_id ?? '',
    expected_delivery_date: detailOrder.expected_delivery_date ?? '',
    order_number: detailOrder.order_number ?? '',
    tracking_number: detailOrder.tracking_number ?? '',
  });
}, [detailOrder]);


  const loadProjectItems = useCallback(
  async (id: Uuid) => {
    setItemsLoading(true);
    setItemsError(null);
    try {
      const response = await fetchProjectItems(id);
      setProjectItems(response);
    } catch (apiError) {
      const message =
        apiError instanceof Error
          ? apiError.message
          : 'No pudimos cargar los items del proyecto.';
      setItemsError(message);
    } finally {
      setItemsLoading(false);
    }
  },
  [fetchProjectItems],
);

const supplierNames = useMemo(() => {
  const map: Record<string, string> = {};
  suppliers.forEach((supplier) => { map[supplier.id] = supplier.name; });
  return map;
}, [suppliers]);
const shippingLocationOptions = useMemo(
  () =>
    shippingLocations.map((shippingLocation) => ({
      label: shippingLocation.name,
      value: shippingLocation.id,
    })),
  [shippingLocations],
);
const projectOrdersTitleCount = ordersPagination.total ?? projectOrders.length;
const projectOrdersTotalPages = Math.max(
  1,
  ordersPagination.total_pages ?? Math.ceil((ordersPagination.total ?? projectOrders.length) / ordersPagination.page_size),
);
const canMoveDetailOrderToShippingStatus = Boolean(
  detailOrder?.supplier_called && detailOrder?.supplier_confirmed,
);

useEffect(() => {
  setOrderPageInput(String(ordersPagination.page));
}, [ordersPagination.page]);

const commitOrderPageInput = () => {
  const parsed = Number(orderPageInput);
  if (!Number.isInteger(parsed)) {
    setOrderPageInput(String(ordersPagination.page));
    return;
  }
  const nextPage = Math.min(projectOrdersTotalPages, Math.max(1, parsed));
  if (nextPage !== ordersPagination.page) void goToOrdersPage(nextPage);
};


    const subprojectProgress = useMemo(() => {
      const grouped = new Map<Uuid, { points: number; count: number }>();

      projectItems.forEach((item) => {
        const points = ITEM_STATE_PROGRESS[item.state] ?? 0;
        const entry = grouped.get(item.subproject_id) ?? { points: 0, count: 0 };

        grouped.set(item.subproject_id, {
          points: entry.points + points,
          count: entry.count + 1,
        });
      });

      return Array.from(grouped.entries()).reduce<Record<Uuid, number>>(
        (acc, [subprojectId, { points, count }]) => {
          acc[subprojectId] = count === 0 ? 0 : Math.round(points / count);
          return acc;
        },
        {},
      );
    }, [projectItems]);

    type NewSubprojectForm = Pick<
      SubprojectPayload,
      'name' | 'type' | 'status' | 'planned_budget' | 'ref' | 'planned_margin'
    > & {
      surface_m2?: DecimalString;
    };

  const emptySubprojectForm: NewSubprojectForm = {
    name: '',
    type: '',
    status: 'planificacion',
    planned_budget: '' as DecimalString,
    surface_m2: undefined,
    ref: '',
    planned_margin: '' as DecimalString,
  };

  const [isCreatingSubproject, setIsCreatingSubproject] = useState(false);
  const [newSubproject, setNewSubproject] = useState<NewSubprojectForm>(emptySubprojectForm);
  const [isSubmittingSubproject, setIsSubmittingSubproject] = useState(false);
  const [createSubprojectError, setCreateSubprojectError] = useState<string | null>(null);

  const handleAddSubproject = () => {
    setCreateSubprojectError(null);
    setNewSubproject(emptySubprojectForm);
    setIsCreatingSubproject(true);
  };

  const handleChangeNewSubproject = <Field extends keyof NewSubprojectForm>(
    field: Field,
    value: NewSubprojectForm[Field],
  ) => {
    setNewSubproject((prev) => ({ ...prev, [field]: value }));
  };


  const formatCurrencyValue = useCallback(
    (value: string | number | null | undefined) => {
      const numeric = toNumber(value);
      return numeric === null ? '—' : formatCurrency(numeric, { showZero: true });
    },
    [formatCurrency],
  );

  const formatPercentValue = useCallback(
    (value: string | number | null | undefined) => {
      const numeric = toNumber(value);
      if (numeric === null) return '—';
      const normalizedPct = numeric > 1 ? numeric : numeric * 100;
      return formatPercent(normalizedPct);
    },
    [formatPercent],
  );

const openNotesPreview = (order: Order) => {
  setNotesPreview(order);
  setNoteDraft(order.notes ?? '');
};

const closeNotesPreview = () => {
  if (savingNote) return;
  setNotesPreview(null);
  setNoteDraft('');
};


  const loadProject = useCallback(
  async (id: Uuid) => {
    setIsLoading(true);
    setError(null);
    try {
      const [detailResponse, itemsResponse] = await Promise.all([
        fetchProjectDetail(id),
        fetchProjectItems(id),
      ]);
      setProjectDetail(detailResponse);
      setItems(itemsResponse);
      setFinances(null);
    } catch (apiError) {
      setError(apiError instanceof Error ? apiError.message : 'No pudimos cargar el detalle del proyecto.');
    } finally {
      setIsLoading(false);
    }
  },
  [fetchProjectDetail, fetchProjectItems],
);

const loadFinances = useCallback(
  async (id: Uuid) => {
    setFinancesLoading(true);
    setFinancesError(null);
    try {
      const response = await fetchProjectFinances(id);
      setFinances(response ?? null);
    } catch (apiError) {
      const message =
        apiError instanceof Error
          ? apiError.message
          : 'No pudimos cargar las finanzas del proyecto.';
      setFinancesError(message);
    } finally {
      setFinancesLoading(false);
    }
  },
  [fetchProjectFinances],
);

const handleEditSubproject = (event: React.MouseEvent<HTMLButtonElement>, subproject: ProjectSubproject) => {
  event.stopPropagation();
  setEditingSubproject(subproject);
  setEditForm({
    name: subproject.name,
    ref: subproject.ref ?? '',
    type: subproject.type,
    status: subproject.status,
    planned_budget: subproject.planned_budget,
    surface_m2: subproject.surface_m2 as DecimalString,
    planned_margin: '' as DecimalString,
  });
  setEditError(null);
};

const validateEditForm = (data: typeof editForm) => {
  if (!data) return 'No hay datos para guardar.';
  if (!data.name.trim()) return 'El nombre es obligatorio.';
  if (!data.type.trim()) return 'El tipo es obligatorio.';
  if (!data.planned_budget || Number(data.planned_budget) < 0) return 'El presupuesto debe ser >= 0.';
  return null;
};

const handleUpdateSubproject = async (event: React.FormEvent<HTMLFormElement>) => {
  event.preventDefault();
  if (!projectId || !editingSubproject || !editForm) return;

  const validation = validateEditForm(editForm);
  if (validation) {
    setEditError(validation);
    return;
  }

  setIsUpdatingSubproject(true);
  setEditError(null);

  const payload: Partial<SubprojectPayload> = {
    name: editForm.name.trim(),
    type: editForm.type.trim(),
    status: editForm.status,
    planned_budget: (editForm.planned_budget || '0') as DecimalString,
    ref: editForm.ref?.trim() || null,
    surface_m2: editForm.surface_m2 || undefined,
  };

  try {
    await updateSubproject(editingSubproject.id, payload);
    toast.success('Subproyecto actualizado correctamente.');
    setEditingSubproject(null);
    setEditForm(null);
    await Promise.all([
      loadSubprojects(projectId),
      loadProject(projectId),
      activeTab === 'items' ? loadProjectItems(projectId) : Promise.resolve()
    ]);
    onSubprojectUpdated?.();
  } catch (apiError) {
    const message = getApiErrorMessage(apiError, 'No pudimos actualizar el subproyecto.');
    setEditError(message);
    toast.error(message);
  } finally {
    setIsUpdatingSubproject(false);
  }
};

useEffect(() => {
  if (!projectId) return;
  void loadProject(projectId);
  void loadSubprojects(projectId);
  void loadProjectItems(projectId);
}, [loadProject, loadProjectItems, loadSubprojects, projectId]);

useEffect(() => {
  if (activeTab !== 'items' || !projectId) return;
  if (projectItems.length > 0 || itemsLoading || itemsError) return;
  void loadProjectItems(projectId);
}, [activeTab, itemsError, itemsLoading, loadProjectItems, projectId, projectItems.length]);

useEffect(() => {
  if (activeTab !== 'finances' || !projectId) return;
  if (finances || financesLoading || financesError) return;
  void loadFinances(projectId);
}, [activeTab, projectId, finances, financesLoading, financesError, loadFinances]);

useEffect(() => {
  if (!projectId) return;
  void loadProject(projectId);
  void loadSubprojects(projectId);
  void loadProjectItems(projectId);
}, [loadProject, loadProjectItems, loadSubprojects, projectId, refreshVersion]);

useEffect(() => {
  if (!projectId) return;
  setOrderFilters((prev) => ({ ...prev, project: projectId }));
  setOrdersPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
}, [projectId, setOrderFilters, setOrdersPagination]);


const budgetDifferenceValue = toNumber(finances?.budget_difference);
const marginDelta = (() => {
  const real = toNumber(finances?.real_margin_pct);
  const estimated = toNumber(finances?.estimated_margin_pct);
  if (real === null || estimated === null) return null;
  return real - estimated;
})();

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 py-12">
        <p className="text-gray-600">{error}</p>
        <Button onClick={() => loadProject(projectId)}>Reintentar</Button>
      </div>
    );
  }

  if (!projectDetail) {
    return null;
  }

  const detailWithDescription = projectDetail as ProjectDetailWithDescription;
  const description = detailWithDescription.description?.trim() ?? '';
  const summary = projectDetail.summary;

  const handleCreateSubproject = async (event: React.FormEvent<HTMLFormElement>) => {
  event.preventDefault();
  if (!projectId) return;

  setIsSubmittingSubproject(true);
  setCreateSubprojectError(null);

  try {
    const payload: SubprojectPayload = {
      project_id: projectId,
      name: newSubproject.name.trim(),
      type: newSubproject.type.trim(),
      status: newSubproject.status,
      planned_budget: (newSubproject.planned_budget || '0') as DecimalString,
    };

    const normalizedRef = newSubproject.ref?.trim();
    if (normalizedRef) payload.ref = normalizedRef;

    if (newSubproject.surface_m2) payload.surface_m2 = newSubproject.surface_m2;
    if (newSubproject.planned_margin) payload.planned_margin = newSubproject.planned_margin;

    await createSubproject(payload);
    toast.success('Subproyecto creado correctamente.');
    setIsCreatingSubproject(false);
    setNewSubproject(emptySubprojectForm);
    await loadSubprojects(projectId);
  } catch (apiError) {
    const message = getApiErrorMessage(apiError, 'No pudimos crear el subproyecto. Inténtalo de nuevo.');
    setCreateSubprojectError(message);
    toast.error(message);
  } finally {
    setIsSubmittingSubproject(false);
  }
};

const handleDelete = async () => {
    if (!confirmDelete) return;
    setSubmitting(true);
  try {
      await deleteOrder(confirmDelete.id);
      toast.success('Pedido eliminado');
      await refreshOrders();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No pudimos eliminar el pedido';
      toast.error(message);
    } finally {
      setSubmitting(false);
      setConfirmDelete(null);
    }
  };

  const handleMarkSupplierCalled = async () => {
    if (!orderPendingCall) return;
    const shippingLocationId = orderPendingCall.shipping_location_id;
    if (!shippingLocationId) {
      toast.error('Añade un lugar de envío antes de registrar la llamada.');
      setOrderPendingCall(null);
      return;
    }
    setMarkingSupplierCalled(true);
  try {
      await updateOrder(orderPendingCall.id, {
        supplier_called: true,
        shipping_location_id: shippingLocationId,
        status: 'pedido',
      });
      toast.success('Llamada registrada');
      await refreshOrders();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No pudimos registrar la llamada';
      toast.error(message);
    } finally {
      setMarkingSupplierCalled(false);
      setOrderPendingCall(null);
    }
  };

  const openOrderEmailDialog = (order: Order) => {
    if (!order.shipping_location_id) {
      toast.error('Añade un lugar de envío antes de enviar el pedido por email.');
      return;
    }
    if (!order.supplier_email) {
      toast.error('El proveedor no tiene email configurado. Actualízalo en Proveedores.');
      return;
    }
    setOrderPendingEmail(order);
  };

  const handleSendOrderEmail = async (payload: { to_email: string; subject: string; body: string; extra_attachments?: File[] }) => {
    if (!orderPendingEmail) return;
    setSendingOrderEmail(true);
    try {
      await sendOrderEmail(orderPendingEmail.id, payload);
      toast.success('Email enviado y pedido marcado.');
      await refreshOrders();
      setOrderPendingEmail(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No pudimos enviar el email del pedido';
      toast.error(message);
    } finally {
      setSendingOrderEmail(false);
    }
  };

  const handleMarkSupplierConfirmed = async () => {
    if (!orderPendingSupplierConfirmation) return;
    if (!orderPendingSupplierConfirmation.supplier_called) {
      toast.error('Debes registrar la llamada antes de confirmar el pedido con el proveedor.');
      setOrderPendingSupplierConfirmation(null);
      return;
    }
    setMarkingSupplierConfirmed(true);
    try {
      await updateOrder(orderPendingSupplierConfirmation.id, {
        supplier_confirmed: true,
        status: 'confirmado',
      });
      toast.success('Confirmación registrada');
      await refreshOrders();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No pudimos registrar la confirmación';
      toast.error(message);
    } finally {
      setMarkingSupplierConfirmed(false);
      setOrderPendingSupplierConfirmation(null);
    }
  };

  const handleDownloadOrderPdf = async (order: Order) => {
    setDownloadingPdfId(order.id);
    try {
      const blob = await downloadOrderPdf(order.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pedido-${order.order_number || order.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No pudimos generar el PDF';
      toast.error(message);
    } finally {
      setDownloadingPdfId(null);
    }
  };

  const handleDetailSave = async () => {
  if (!detailOrder) return;

  const nextStatus = detailForm.status;
  const isStatusChanging = nextStatus !== detailOrder.status;
  const isShippingStatus = nextStatus === 'enviado' || nextStatus === 'recibido';
  const canMoveToShippingStatus = detailOrder.supplier_called && detailOrder.supplier_confirmed;

  if (isStatusChanging && !['enviado', 'recibido', 'cancelado'].includes(nextStatus)) {
    toast.error('Desde este detalle solo puedes cambiar el estado a enviado, recibido o cancelado.');
    return;
  }

  if (isShippingStatus && !canMoveToShippingStatus) {
    toast.error('Para poner el pedido en enviado o recibido, primero registra llamada y confirmación.');
    return;
  }
  if (detailForm.shipping_location_name.trim() && !detailForm.shipping_location_id) {
    toast.error('Selecciona un lugar de envío válido.');
    return;
  }

  setSavingDetail(true);
  try {
    await updateOrder(detailOrder.id, {
  status: nextStatus,
  shipping_location_id: detailForm.shipping_location_id || null,
  expected_delivery_date: detailForm.expected_delivery_date || null,
  order_number: detailForm.order_number.trim(),
  tracking_number: detailForm.tracking_number.trim() || null,
});
    toast.success('Pedido actualizado');
    await refreshOrders();
    setDetailOrder(null);
  } catch (err) {
    const message = err instanceof Error ? err.message : 'No pudimos actualizar el pedido';
    toast.error(message);
  } finally {
    setSavingDetail(false);
  }
};

const handleSaveNote = async () => {
  if (!notesPreview) return;
  setSavingNote(true);
  try {
    await updateOrder(notesPreview.id, { notes: noteDraft.trim() || null });
    toast.success('Notas actualizadas');
    await refreshOrders();
    closeNotesPreview();
  } catch (err) {
    const message = err instanceof Error ? err.message : 'No pudimos guardar la nota';
    toast.error(message);
  } finally {
    setSavingNote(false);
  }
};

const marginDeltaColor =
  marginDelta === null ? 'text-gray-500' : marginDelta >= 0 ? 'text-green-600' : 'text-red-600';
const budgetDifferenceColor =
  budgetDifferenceValue === null
    ? 'text-gray-500'
    : budgetDifferenceValue <= 0
      ? 'text-green-600'
      : 'text-red-600';

const shouldShowMarginWarning = marginDelta !== null && marginDelta < 0;
const marginWarningMessage = shouldShowMarginWarning
  ? 'El margen real está por debajo del estimado. Revisa costes e ingresos para corregirlo.'
  : 'El margen real está igual o por encima del estimado.';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={onBack} className="p-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl text-gray-900">{projectDetail.name}</h1>
            <p className="text-gray-600">{projectDetail.client}</p>
          </div>
        </div>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 gap-6 text-center md:grid-cols-2 lg:grid-cols-6">
            <div>
              <p className="mb-1 text-sm text-gray-600">Tipo</p>
              <p className="text-gray-900">{projectDetail.type}</p>
            </div>
            <div>
              <p className="mb-1 text-sm text-gray-600">Dirección</p>
              <p className="text-gray-900">{projectDetail.address}</p>
            </div>
            <div>
              <p className="mb-1 text-sm text-gray-600">Estado</p>
              <Badge variant={STATUS_BADGE_VARIANTS[projectDetail.status] ?? 'secondary'}>
                {formatProjectStatus(projectDetail.status)}
              </Badge>
            </div>
            <div>
              <p className="mb-1 text-sm text-gray-600">Presupuesto Planificado</p>
              <p className="text-xl text-gray-900">{formatCurrencyValue(projectDetail.total_budget)}</p>
            </div>
            <div>
              <p className="mb-1 text-sm text-gray-600">PVP Total</p>
              <p className="text-xl text-gray-900">{formatCurrencyValue(summary.actual_budget_total)}</p>
            </div>
            <div>
              <p className="mb-1 text-sm text-gray-600">Margen Real</p>
              <p className="text-xl text-green-600">{formatPercentValue(summary.real_margin_avg)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="subprojects" value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="subprojects">Subproyectos</TabsTrigger>
          <TabsTrigger value="items">Items/Muebles</TabsTrigger>
          <TabsTrigger value="orders">Pedidos</TabsTrigger>
          <TabsTrigger value="finances">Finanzas</TabsTrigger>
        </TabsList>

                <TabsContent value="subprojects">
          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center space-x-3">
                <Building className="h-5 w-5 text-gray-600" />
                <CardTitle>Subproyectos del Edificio</CardTitle>
              </div>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={handleAddSubproject}>
                <Plus className="mr-2 h-4 w-4" />
                Añadir subproyecto
              </Button>
            </CardHeader>
            <CardContent>
              {subprojectsError && (
                <div className="mb-4 flex flex-col items-center gap-3 rounded-md bg-red-50 p-4 text-center text-sm text-red-700">
                  <p>{subprojectsError}</p>
                  <Button size="sm" variant="outline" onClick={() => loadSubprojects(projectId)}>
                    Reintentar
                  </Button>
                </div>
              )}

              {subprojectsLoading ? (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <Card key={index} className="border border-dashed border-gray-200">
                      <CardContent className="space-y-4 p-4">
                        <Skeleton className="h-5 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                        <Skeleton className="h-16 w-full" />
                        <Skeleton className="h-9 w-full" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : subprojects.length === 0 ? (
                <p className="text-center text-sm text-gray-500">Este proyecto aún no tiene subproyectos.</p>
              ) : (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {subprojects.map((subproject) => {
                    const surfaceValue = toNumber(subproject.surface_m2);
                    const completionFromItems = subprojectProgress[subproject.id];
                    const completion = completionFromItems ?? 0;

                    return (
                      <Card
                        key={subproject.id}
                        className="cursor-pointer border border-gray-200 transition-colors hover:border-blue-300"
                        onClick={() => onSelectSubproject?.(subproject.id)}
                      >
                        <CardContent className="p-4">
                          <div className="mb-4 flex flex-col items-center gap-2 text-center md:flex-row md:justify-between md:text-left">
                            <div>
                              <h3 className="text-gray-900">{subproject.name}</h3>
                              <p className="text-sm text-gray-600">{subproject.type}</p>
                            </div>
                            <Badge variant={STATUS_BADGE_VARIANTS[subproject.status] ?? 'secondary'}>
                              {formatProjectStatus(subproject.status)}
                            </Badge>
                          </div>

                          <div className="mb-4 grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-600">Superficie</p>
                              <p className="text-gray-900">
                                {surfaceValue === null
                                  ? '—'
                                  : `${surfaceValue.toLocaleString(appSettings.currencyLocale)} m²`}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600">Presupuesto planificado</p>
                              <p className="text-gray-900">{formatCurrencyValue(subproject.planned_budget)}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">PVP Total</p>
                              <p className="text-gray-900">{formatCurrencyValue(subproject.actual_budget)}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Estancias</p>
                              <div className="flex items-center">
                                <Home className="mr-1 h-4 w-4 text-gray-500" />
                                <span className="text-gray-900">{subproject.rooms_count}</span>
                              </div>
                            </div>
                          </div>

                          <div className="mb-4">
                            <p className="mb-2 text-sm text-gray-600">Progreso real</p>
                            <div className="flex items-center gap-2">
                              <div className="h-2 flex-1 rounded-full bg-gray-200">
                                <div
                                  className={`h-2 rounded-full ${getCompletionColor(completion)}`}
                                  style={{ width: `${completion}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-600">{completion}%</span>
                            </div>
                          </div>

                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={(event) => handleEditSubproject(event, subproject)}
                          >
                            Editar subproyecto
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        <TabsContent value="items">
          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Items del Proyecto</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre subproyecto</TableHead>
                    <TableHead>Referencia item</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead className="text-center">Nº items</TableHead>
                    <TableHead className="text-center">Coste real</TableHead>
                    <TableHead className="text-center">PVP Total</TableHead>
                    <TableHead className="text-center">Margen real</TableHead>
                    <TableHead className="text-center">Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {itemsLoading && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-600">
                          <Loader2 className="h-4 w-4 animate-spin text-gray-500" /> Cargando items...
                        </div>
                      </TableCell>
                    </TableRow>
                  )}

                  {itemsError && !itemsLoading && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <div className="flex items-center justify-between gap-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
                          <span>{itemsError}</span>
                          <Button size="sm" variant="outline" onClick={() => projectId && loadProjectItems(projectId)}>
                            Reintentar
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}

                  {!itemsLoading &&
                    !itemsError &&
                    projectItems.map((item) => {
                      const cost = toNumber(item.real_cost_total);
                      const pvp = toNumber(item.net_total_pvp);
                      const marginPct = cost === null || pvp === null || pvp === 0 ? null : (pvp - cost) / pvp;

                      return (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium text-gray-900">{item.subproject_name}</TableCell>
                          <TableCell className="text-blue-600">{item.ref}</TableCell>
                          <TableCell>{item.description}</TableCell>
                          <TableCell>{item.supplier}</TableCell>
                          <TableCell className="text-center">{item.quantity ?? '—'}</TableCell>
                          <TableCell className="text-center">{formatCurrencyValue(cost)}</TableCell>
                          <TableCell className="text-center">{formatCurrencyValue(pvp)}</TableCell>
                          <TableCell className="text-center text-green-600">{formatPercentValue(marginPct)}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant={ITEM_STATE_BADGES[item.state] ?? 'secondary'}>
                              {item.state.charAt(0).toUpperCase() + item.state.slice(1)}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                  {!itemsLoading && !itemsError && projectItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-sm text-gray-500">
                        Sin items registrados para este proyecto.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              </div>
            </CardContent>
          </Card>
        </TabsContent>

<TabsContent value="orders">
  <Card className="shadow-sm">
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle>Pedidos del Proyecto {ordersLoading ? '(cargando...)' : `(${projectOrdersTitleCount})`}</CardTitle>
    </CardHeader>

    <CardContent>
      {ordersError && (
        <div className="mb-4 flex items-center justify-between rounded-md bg-red-50 p-4 text-sm text-red-700">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span>{ordersError}</span>
          </div>
          <Button variant="outline" size="sm" onClick={refreshOrders} disabled={ordersLoading}>
            Reintentar
          </Button>
        </div>
      )}

      <Table className="table-fixed w-full [&_th]:box-border [&_td]:box-border">
        <TableHeader>
          <TableRow>
            <TableHead style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.orderRef }} className="!whitespace-normal break-words text-left">
              Nº Pedido
            </TableHead>
            <TableHead style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.subproject }} className="!whitespace-normal break-words text-left">
              Subproyecto
            </TableHead>
            <TableHead style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.supplier }} className="!whitespace-normal break-words text-left">
              Proveedor
            </TableHead>
            <TableHead style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.items }} className="text-center">
              Items
            </TableHead>
            <TableHead style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.total }} className="text-center">
              Total
            </TableHead>
            <TableHead style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.status }} className="text-center">
              Estado
            </TableHead>
            <TableHead style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.supplierEmailed }} className="text-center">
              Email
            </TableHead>
            <TableHead style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.supplierCalled }} className="text-center">
              ¿Has llamado?
            </TableHead>
            <TableHead style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.supplierConfirmed }} className="text-center">
              Confirmación
            </TableHead>
            <TableHead style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.shipping }} className="!whitespace-normal break-words text-center">
              Lugar de envío
            </TableHead>
            <TableHead style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.tracking }} className="!whitespace-normal break-words text-center">
              Nº seguimiento
            </TableHead>
            <TableHead style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.delivery }} className="text-center">
              Entrega prevista
            </TableHead>
            <TableHead style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.notes }} className="text-center">
              Notas
            </TableHead>
            <TableHead style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.pdf }} className="text-center">
              PDF
            </TableHead>
            <TableHead style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.actions }} className="text-center !whitespace-normal break-words">
              Acciones
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {ordersLoading ? (
            Array.from({ length: 4 }).map((_, idx) => (
              <TableRow key={idx}>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.orderRef }}><Skeleton className="h-4 w-24" /></TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.subproject }}><Skeleton className="h-4 w-36" /></TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.supplier }}><Skeleton className="h-4 w-28" /></TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.items }} className="text-center"><Skeleton className="mx-auto h-5 w-10" /></TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.total }} className="text-center"><Skeleton className="mx-auto h-5 w-14" /></TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.status }} className="text-center"><Skeleton className="mx-auto h-5 w-20" /></TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.supplierEmailed }} className="text-center"><Skeleton className="mx-auto h-5 w-14" /></TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.supplierCalled }} className="text-center"><Skeleton className="mx-auto h-5 w-14" /></TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.supplierConfirmed }} className="text-center"><Skeleton className="mx-auto h-5 w-14" /></TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.shipping }}><Skeleton className="h-4 w-36" /></TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.tracking }} className="text-center"><Skeleton className="mx-auto h-5 w-24" /></TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.delivery }} className="text-center"><Skeleton className="mx-auto h-5 w-20" /></TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.notes }} className="text-center"><Skeleton className="mx-auto h-5 w-16" /></TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.pdf }} className="text-center"><Skeleton className="mx-auto h-5 w-14" /></TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.actions }} className="text-center"><Skeleton className="mx-auto h-5 w-24" /></TableCell>
              </TableRow>
            ))
          ) : projectOrders.length === 0 ? (
            <TableRow>
              <TableCell colSpan={15} className="py-8 text-center text-gray-500">
                No hay pedidos para este proyecto.
              </TableCell>
            </TableRow>
          ) : (
            projectOrders.map((order) => (
              <TableRow key={order.id} className="hover:bg-gray-50">
                <TableCell
                  style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.orderRef }}
                  className="!whitespace-normal break-words text-blue-600 align-top"
                >
                  {formatOrderRef(order)}
                </TableCell>
                <TableCell
                  style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.subproject }}
                  className="text-gray-900 align-top whitespace-normal break-words leading-tight"
                >
                  <SubprojectLink
                    projectId={order.project_id}
                    subprojectId={order.subproject_id}
                    label={order.subproject_name ?? '—'}
                    canNavigate={!isLogistica}
                    disabledTitle={isLogistica ? 'Sin acceso al subproyecto' : undefined}
                    textClassName="text-gray-900"
                  />
                </TableCell>
                <TableCell
                  style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.supplier }}
                  className="text-gray-900 align-top whitespace-normal break-words leading-tight"
                >
                  {order.supplier_name ?? supplierNames[order.supplier_id] ?? order.supplier_id}
                </TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.items }} className="text-center align-top">
                  <Badge variant="outline" className="px-2 py-1 text-xs">
                    {order.items?.length ?? 0}
                  </Badge>
                </TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.total }} className="text-center text-gray-900 align-top">
                  {formatCurrency(order.total_cost)}
                </TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.status }} className="text-center align-top">
                  <Badge variant={STATUS_VARIANTS[order.status]} className="px-2 py-1">
                    {STATUS_LABELS[order.status]}
                  </Badge>
                </TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.supplierEmailed }} className="text-center align-top">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={
                      order.supplier_emailed ||
                      sendingOrderEmail ||
                      markingSupplierCalled ||
                      markingSupplierConfirmed
                    }
                    onClick={() => openOrderEmailDialog(order)}
                    aria-label={order.supplier_emailed ? 'Email enviado' : 'Email pendiente'}
                    title={order.supplier_emailed ? 'Email enviado' : 'Email pendiente'}
                  >
                    {order.supplier_emailed ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <X className="h-4 w-4 text-red-500" />
                    )}
                    <span className="sr-only">{order.supplier_emailed ? 'Email enviado' : 'Email pendiente'}</span>
                  </Button>
                </TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.supplierCalled }} className="text-center align-top">
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={
                        order.supplier_called ||
                        markingSupplierCalled ||
                        markingSupplierConfirmed ||
                        !order.shipping_location_id
                      }
                      onClick={() => setOrderPendingCall(order)}
                      aria-label={order.supplier_called ? 'Llamada registrada' : 'Sin llamada'}
                      title={order.supplier_called ? 'Llamada registrada' : 'Sin llamada'}
                  >
                    {order.supplier_called ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <X className="h-4 w-4 text-red-500" />
                    )}
                    <span className="sr-only">{order.supplier_called ? 'Llamada registrada' : 'Sin llamada'}</span>
                  </Button>
                </TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.supplierConfirmed }} className="text-center align-top">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={
                      order.supplier_confirmed ||
                      markingSupplierConfirmed ||
                      markingSupplierCalled ||
                      !order.supplier_called
                    }
                    onClick={() => setOrderPendingSupplierConfirmation(order)}
                    aria-label={order.supplier_confirmed ? 'Pedido confirmado con proveedor' : 'Sin confirmar'}
                    title={order.supplier_confirmed ? 'Pedido confirmado con proveedor' : 'Sin confirmar'}
                  >
                    {order.supplier_confirmed ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <X className="h-4 w-4 text-red-500" />
                    )}
                    <span className="sr-only">
                      {order.supplier_confirmed ? 'Pedido confirmado con proveedor' : 'Sin confirmar'}
                    </span>
                  </Button>
                </TableCell>
                <TableCell
                  style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.shipping }}
                  className="!whitespace-normal break-words text-center text-gray-700 align-top"
                >
                  {order.shipping_location ?? '—'}
                </TableCell>
                <TableCell
                  style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.tracking }}
                  className="!whitespace-normal break-words text-center text-gray-700 align-top"
                >
                  {order.tracking_number ?? '—'}
                </TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.delivery }} className="text-center text-gray-700 align-top">
                  {formatDate(order.expected_delivery_date)}
                </TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.notes }} className="text-center align-top">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`inline-flex items-center gap-1 border px-2 py-1 ${
                      order.notes?.trim()
                        ? 'border-orange-300 bg-orange-50 text-orange-600'
                        : 'border-gray-200 bg-gray-50 text-gray-500'
                    }`}
                    title={order.notes ?? 'Sin notas'}
                    onClick={() => openNotesPreview(order)}
                  >
                    <StickyNote className="h-4 w-4" />
                  </Button>
                </TableCell>
                <TableCell style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.pdf }} className="text-center align-top">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="inline-flex items-center gap-1 border px-2 py-1 text-gray-700"
                    onClick={() => handleDownloadOrderPdf(order)}
                    disabled={downloadingPdfId === order.id || ordersLoading}
                    title="Descargar PDF del pedido"
                  >
                    {downloadingPdfId === order.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                </TableCell>
                <TableCell
                  style={{ width: PROJECT_ORDERS_COLUMN_WIDTHS.actions }}
                  className="text-center align-top !whitespace-normal break-words"
                >
                  <div className="flex flex-col items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full whitespace-normal break-words leading-tight"
                      onClick={() => setDetailOrder(order)}
                    >
                      Ver detalle
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full whitespace-normal break-words leading-tight text-red-600 hover:text-red-700"
                      onClick={() => setConfirmDelete(order)}
                    >
                      Eliminar
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => goToOrdersPage(ordersPagination.page - 1)}
          disabled={ordersPagination.page <= 1 || ordersLoading}
        >
          Anterior
        </Button>
        <div className="flex items-center gap-2">
          <Input
            value={orderPageInput}
            onChange={(event) => setOrderPageInput(event.target.value)}
            onBlur={commitOrderPageInput}
            onKeyDown={(event) => {
              if (event.key === 'Enter') commitOrderPageInput();
            }}
            className="h-9 w-16 text-center"
            disabled={ordersLoading}
          />
          <span className="text-sm text-gray-600">/ {projectOrdersTotalPages}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => goToOrdersPage(ordersPagination.page + 1)}
          disabled={ordersLoading || ordersPagination.page >= projectOrdersTotalPages}
        >
          Siguiente
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => goToOrdersPage(projectOrdersTotalPages)}
          disabled={ordersLoading || ordersPagination.page >= projectOrdersTotalPages}
        >
          Última página
        </Button>
        <Select
          value={String(ordersPagination.page_size)}
          onValueChange={(value) => {
            const nextSize = Number(value);
            setOrdersPagination((prev) => ({ ...prev, page: 1, page_size: nextSize }));
          }}
          disabled={ordersLoading}
        >
          <SelectTrigger className="w-24">
            <SelectValue placeholder={String(ordersPagination.page_size)} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="5">5</SelectItem>
            <SelectItem value="10">10</SelectItem>
            <SelectItem value="50">50</SelectItem>
          </SelectContent>
        </Select>
        {ordersLoading ? <Loader2 className="h-4 w-4 animate-spin text-gray-500" /> : null}
      </div>
    </CardContent>
  </Card>

  {/* Modales reutilizados */}
  <AlertDialog open={Boolean(confirmDelete)} onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>¿Eliminar pedido?</AlertDialogTitle>
        <AlertDialogDescription className="space-y-2 text-gray-700">
          <div>Eliminarás definitivamente el pedido {confirmDelete ? formatOrderRef(confirmDelete) : ''}.</div>
          <div>Esta acción no se puede deshacer.</div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={submitting}>Cancelar</AlertDialogCancel>
        <AlertDialogAction className="bg-red-600 hover:bg-red-700" disabled={submitting} onClick={handleDelete}>
          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
          Eliminar
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <AlertDialog open={Boolean(orderPendingCall)} onOpenChange={(open) => { if (!open) setOrderPendingCall(null); }}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>¿Registrar llamada al proveedor?</AlertDialogTitle>
        <AlertDialogDescription className="space-y-2 text-gray-700">
          <div>¿Has llamado al proveedor del pedido {orderPendingCall ? formatOrderRef(orderPendingCall) : ''}?</div>
          <div>Esta acción marcará el pedido como "pedido".</div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={markingSupplierCalled}>Cancelar</AlertDialogCancel>
        <AlertDialogAction className="bg-green-600 hover:bg-green-700" disabled={markingSupplierCalled} onClick={handleMarkSupplierCalled}>
          {markingSupplierCalled ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
          Registrar llamada
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
  <AlertDialog
    open={Boolean(orderPendingSupplierConfirmation)}
    onOpenChange={(open) => { if (!open) setOrderPendingSupplierConfirmation(null); }}
  >
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>¿Registrar confirmación con el proveedor?</AlertDialogTitle>
        <AlertDialogDescription className="space-y-2 text-gray-700">
          <div>
            ¿Has confirmado con el proveedor el pedido{' '}
            {orderPendingSupplierConfirmation ? formatOrderRef(orderPendingSupplierConfirmation) : ''}?
          </div>
          <div>Esta acción marcará el pedido como "confirmado".</div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={markingSupplierConfirmed}>Cancelar</AlertDialogCancel>
        <AlertDialogAction
          className="bg-green-600 hover:bg-green-700"
          disabled={markingSupplierConfirmed}
          onClick={handleMarkSupplierConfirmed}
        >
          {markingSupplierConfirmed ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
          Registrar confirmación
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <Dialog open={Boolean(detailOrder)} onOpenChange={(open) => { if (!open && !savingDetail) setDetailOrder(null); }}>
    <DialogContent className="w-full min-w-[1000px] max-w-5xl max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Detalle del pedido {detailOrder ? formatOrderRef(detailOrder) : ''}</DialogTitle>
        <DialogDescription>
          {detailOrder
            ? `${detailOrder.project_name ?? '—'} / ${detailOrder.subproject_name ?? '—'} · ${
                detailOrder.supplier_name ?? supplierNames[detailOrder.supplier_id] ?? '—'
              }`
            : ''}
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <div className="grid gap-2">
            <label className="text-sm font-medium text-gray-700">Nombre del pedido</label>
            <Input
              value={detailForm.order_number}
              onChange={(event) => setDetailForm((prev) => ({ ...prev, order_number: event.target.value }))}
              placeholder="Ej: PED-1234 o tu referencia interna"
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium text-gray-700">Número de seguimiento</label>
            <Input
              value={detailForm.tracking_number}
              onChange={(event) => setDetailForm((prev) => ({ ...prev, tracking_number: event.target.value }))}
              placeholder="Opcional"
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium text-gray-700">Estado</label>
            <Select
              value={detailForm.status}
              onValueChange={(value) => setDetailForm((prev) => ({ ...prev, status: value as OrderStatus }))}
            >
              <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="borrador" disabled>Borrador</SelectItem>
                <SelectItem value="pedido" disabled>Pedido</SelectItem>
                <SelectItem value="confirmado" disabled>Confirmado</SelectItem>
                <SelectItem value="enviado" disabled={!canMoveDetailOrderToShippingStatus}>Enviado</SelectItem>
                <SelectItem value="recibido" disabled={!canMoveDetailOrderToShippingStatus}>Recibido</SelectItem>
                <SelectItem value="cancelado">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium text-gray-700">Lugar de envío</label>
            <LookupAutocomplete
              value={detailForm.shipping_location_name}
              options={shippingLocationOptions}
              loading={shippingLocationsLoading}
              placeholder="Busca y selecciona un lugar de envío"
              onSearch={(query) => {
                void refreshShippingLocations({
                  search: query.trim() || undefined,
                  active: true,
                  limit: 200,
                });
              }}
              onTextChange={(next) =>
                setDetailForm((prev) => ({
                  ...prev,
                  shipping_location_name: next,
                  shipping_location_id: '',
                }))
              }
              onSelect={({ label, value }) =>
                setDetailForm((prev) => ({
                  ...prev,
                  shipping_location_name: label,
                  shipping_location_id: value,
                }))
              }
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium text-gray-700">Entrega prevista</label>
            <Input
              type="date"
              value={detailForm.expected_delivery_date}
              onChange={(event) => setDetailForm((prev) => ({ ...prev, expected_delivery_date: event.target.value }))}
            />
          </div>
        </div>

        <div className="rounded-lg border">
          <Table className="table-fixed min-w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="!whitespace-normal break-words">Descripción</TableHead>
                <TableHead className="text-center whitespace-nowrap">Cantidad</TableHead>
                <TableHead className="text-center whitespace-nowrap">Coste unitario</TableHead>
                <TableHead className="text-center whitespace-nowrap">Total línea</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detailOrder?.items?.length ? (
                detailOrder.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="text-gray-900 whitespace-normal break-words">{item.description}</TableCell>
                    <TableCell className="text-center whitespace-nowrap">{item.quantity}</TableCell>
                    <TableCell className="text-center whitespace-nowrap">{formatCurrency(item.unit_cost)}</TableCell>
                    <TableCell className="text-center whitespace-nowrap">{formatCurrency(item.line_total_cost)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="py-6 text-center text-gray-500">
                    Este pedido aún no tiene líneas.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => setDetailOrder(null)} disabled={savingDetail}>Cerrar</Button>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleDetailSave} disabled={savingDetail}>
          Guardar cambios
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <Dialog open={Boolean(notesPreview)} onOpenChange={(open) => { if (!open && !savingNote) closeNotesPreview(); }}>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>Notas para {notesPreview ? formatOrderRef(notesPreview) : ''}</DialogTitle>
        <DialogDescription>Observaciones internas del pedido.</DialogDescription>
      </DialogHeader>
      <Textarea
        value={noteDraft}
        onChange={(e) => setNoteDraft(e.target.value)}
        rows={5}
        disabled={savingNote}
        placeholder="Observaciones para este pedido"
        className="min-h-[240px] max-h-[400px] resize-y overflow-x-hidden break-all whitespace-pre-wrap"
      />
      <DialogFooter>
        <Button variant="outline" onClick={closeNotesPreview} disabled={savingNote}>Cancelar</Button>
        <Button onClick={handleSaveNote} disabled={savingNote || noteDraft === (notesPreview?.notes ?? '')}>
          {savingNote ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Guardar
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  <OrderEmailDialog
    open={Boolean(orderPendingEmail)}
    order={orderPendingEmail}
    sending={sendingOrderEmail}
    onOpenChange={(open) => {
      if (!open && !sendingOrderEmail) setOrderPendingEmail(null);
    }}
    onSend={handleSendOrderEmail}
  />
</TabsContent>


        <TabsContent value="finances">
  {financesLoading && (
  <div className="flex min-h-[200px] items-center justify-center">
    <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
  </div>
)}

  {financesError && (
    <Alert variant="destructive" className="mb-4">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>No pudimos cargar las finanzas</AlertTitle>
      <AlertDescription className="flex flex-col gap-2">
        {financesError}
        <Button size="sm" variant="outline" onClick={() => projectId && loadFinances(projectId)}>
          Reintentar
        </Button>
      </AlertDescription>
    </Alert>
  )}

  {!financesLoading && !financesError && !finances && (
    <Card className="shadow-sm">
      <CardContent className="py-10 text-center text-sm text-gray-500">
        Sin información financiera disponible
      </CardContent>
    </Card>
  )}

  {finances && (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Coste total</CardTitle>
            <p className="text-sm text-gray-500">Costes reales + montajes + extras</p>
          </CardHeader>
          <CardContent className="text-2xl font-semibold text-gray-900">
            {formatCurrencyValue(finances.total_cost)}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Total extras</CardTitle>
            <p className="text-sm text-gray-500">Gastos adicionales de subproyectos</p>
          </CardHeader>
          <CardContent className="text-2xl font-semibold text-gray-900">
            {formatCurrencyValue(finances.total_extras)}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>PVP total</CardTitle>
            <p className="text-sm text-gray-500">Ventas netas de items</p>
          </CardHeader>
          <CardContent className="text-2xl font-semibold text-gray-900">
            {formatCurrencyValue(finances.total_pvp)}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Margen bruto</CardTitle>
            <p className="text-sm text-gray-500">Ingresos - costes</p>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-green-600">
              {formatCurrencyValue(finances.gross_margin_amount)}
            </p>
            <p className="text-sm text-gray-500">
              {formatPercentValue(finances.gross_margin_pct)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>IVA (21%)</CardTitle>
            <p className="text-sm text-gray-500">Calculado</p>
          </CardHeader>
          <CardContent className="text-2xl font-semibold text-gray-900">
            {formatCurrencyValue(finances.iva_amount)}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Total con IVA</CardTitle>
            <p className="text-sm text-gray-500">PVP neto + IVA</p>
          </CardHeader>
          <CardContent className="text-2xl font-semibold text-gray-900">
            {formatCurrencyValue(finances.total_with_iva)}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Margen vs estimado</CardTitle>
            <p className="text-sm text-gray-500">Real – estimado (pp)</p>
          </CardHeader>
          <CardContent>
            <p className={`text-2xl font-semibold ${marginDeltaColor}`}>
              {marginDelta === null ? '—' : `${marginDelta >= 0 ? '+' : ''}${marginDelta.toFixed(2)} pp`}
            </p>
            <p className="text-sm text-gray-500">Estimado: {formatPercentValue(finances.estimated_margin_pct)}</p>
            <p className="text-sm text-gray-500">Real: {formatPercentValue(finances.real_margin_pct)}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Diferencia presupuestaria</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-baseline justify-between">
            <span className="text-gray-600">PVP Total Vs. presupuesto planificado</span>
            <span className={`text-2xl font-semibold text-black-500`}>
              {formatCurrencyValue(finances.budget_difference)}
            </span>
          </div>
          {shouldShowMarginWarning && (
            <div className="rounded-lg bg-yellow-50 p-3">
              <p className="text-sm text-yellow-800">{marginWarningMessage}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )}
</TabsContent>


      </Tabs>
      <Dialog
        open={isCreatingSubproject}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && isSubmittingSubproject) return;
          setIsCreatingSubproject(nextOpen);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Añadir subproyecto</DialogTitle>
            <DialogDescription>Introduce los datos básicos y lo añadiremos a este proyecto.</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleCreateSubproject}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="subproject-ref">Referencia</Label>
                <Input
                  id="subproject-ref"
                  value={newSubproject.ref ?? ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeNewSubproject('ref', event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subproject-status">Estado</Label>
                <select id="subproject-status" className="input"
                  value={newSubproject.status}
                  onChange={(event) =>
                    handleChangeNewSubproject('status', event.target.value as NewSubprojectForm['status'])}>
                  <option value="planificacion">Planificación</option>
                  <option value="en_progreso">En progreso</option>
                  <option value="presupuestado">Presupuestado</option>
                  <option value="finalizado">Finalizado</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="subproject-name">Nombre</Label>
              <Input id="subproject-name" value={newSubproject.name}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeNewSubproject('name', event.target.value)} required/>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
              <Label htmlFor="subproject-type">Tipo</Label>
              <Input id="subproject-type" value={newSubproject.type}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeNewSubproject('type', event.target.value)} required/>
            </div>
              <div className="space-y-2">
                <Label htmlFor="subproject-surface">Superficie (m²)</Label>
                <Input id="subproject-surface" type="number" min="0" step="0.01"
                  value={newSubproject.surface_m2 ?? ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeNewSubproject('surface_m2', event.target.value as DecimalString)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subproject-budget">Presupuesto planificado (€)</Label>
                <Input id="subproject-budget" type="number" min="0" step="0.01"
                  value={newSubproject.planned_budget}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeNewSubproject('planned_budget', event.target.value as DecimalString)} required/>
              </div>
              <div className="space-y-2">
              <Label htmlFor="subproject-margin">Margen planificado (%)</Label>
              <Input
                id="subproject-margin"
                type="number"
                step="0.01"
                value={newSubproject.planned_margin ?? ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeNewSubproject('planned_margin', event.target.value as DecimalString)}
              />
              </div>

            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">

            </div>

            {createSubprojectError && <p className="text-sm text-red-600">{createSubprojectError}</p>}

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setIsCreatingSubproject(false)} disabled={isSubmittingSubproject}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmittingSubproject}>
                {isSubmittingSubproject ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(editingSubproject)}
        onOpenChange={(open) => {
          if (!open && isUpdatingSubproject) return;
          if (!open) { setEditingSubproject(null); setEditForm(null); }
        }}
      >
  <DialogContent>
    <DialogHeader>
      <DialogTitle>Editar subproyecto</DialogTitle>
      <DialogDescription>Actualiza los datos básicos y guardaremos los cambios.</DialogDescription>
    </DialogHeader>
    <form className="space-y-4" onSubmit={handleUpdateSubproject}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="edit-ref">Referencia</Label>
          <Input id="edit-ref" value={editForm?.ref ?? ''} onChange={(e) => setEditForm((prev) => prev ? { ...prev, ref: e.target.value } : prev)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="edit-status">Estado</Label>
          <select id="edit-status" className="input" value={editForm?.status ?? 'planificacion'} onChange={(e) => setEditForm((prev) => prev ? { ...prev, status: e.target.value as NewSubprojectForm['status'] } : prev)}>
            <option value="planificacion">Planificación</option>
            <option value="en_progreso">En progreso</option>
            <option value="presupuestado">Presupuestado</option>
            <option value="finalizado">Finalizado</option>
          </select>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="edit-name">Nombre</Label>
        <Input id="edit-name" value={editForm?.name ?? ''} onChange={(e) => setEditForm((prev) => prev ? { ...prev, name: e.target.value } : prev)} required />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="edit-type">Tipo</Label>
          <Input id="edit-type" value={editForm?.type ?? ''} onChange={(e) => setEditForm((prev) => prev ? { ...prev, type: e.target.value } : prev)} required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="edit-surface">Superficie (m²)</Label>
          <Input id="edit-surface" type="number" min="0" step="0.01" value={editForm?.surface_m2 ?? ''} onChange={(e) => setEditForm((prev) => prev ? { ...prev, surface_m2: e.target.value as DecimalString } : prev)} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="edit-budget">Presupuesto planificado (€)</Label>
          <Input id="edit-budget" type="number" min="0" step="0.01" value={editForm?.planned_budget ?? ''} onChange={(e) => setEditForm((prev) => prev ? { ...prev, planned_budget: e.target.value as DecimalString } : prev)} required />
        </div>
      </div>
      {editError && <p className="text-sm text-red-600">{editError}</p>}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={() => { setEditingSubproject(null); setEditForm(null); }} disabled={isUpdatingSubproject}>Cancelar</Button>
        <Button type="submit" disabled={isUpdatingSubproject}>{isUpdatingSubproject ? 'Guardando…' : 'Guardar cambios'}</Button>
      </div>
    </form>
  </DialogContent>
</Dialog>

    </div>
  );
}
